import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Paper,
  useTheme,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Chip
} from '@mui/material';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WbCloudyIcon from '@mui/icons-material/WbCloudy';
import UmbrellaIcon from '@mui/icons-material/Umbrella';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import weatherService from '../../services/weatherService';

/**
 * WeatherSearch Component - Allows searching for weather information by location
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Function to close the dialog
 * @param {Function} onSelectWeather - Function to select weather information
 * @param {string} initialLocation - Initial location to search for
 * @returns {JSX.Element} - Rendered component
 */
const WeatherSearch = ({ open, onClose, onSelectWeather, initialLocation = '' }) => {
  const [location, setLocation] = useState(initialLocation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentWeather, setCurrentWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [selectedForecast, setSelectedForecast] = useState(null); // { dateKey, slot }
  const theme = useTheme();

  // Reset state when dialog opens with new initial location
  useEffect(() => {
    if (open && initialLocation) {
      setLocation(initialLocation);
      if (initialLocation.trim()) {
        handleSearch(initialLocation);
      }
    }
  }, [open, initialLocation]);

  // Clear copy success message after 2 seconds
  useEffect(() => {
    if (copySuccess) {
      const timer = setTimeout(() => setCopySuccess(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copySuccess]);

  const handleSearch = useCallback(async (searchLocation = location) => {
    if (!searchLocation.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Get current weather
      const weatherData = await weatherService.getCurrentWeather(searchLocation);
      setCurrentWeather(weatherData);
      
      // Get forecast data
      const forecastData = await weatherService.getForecast(searchLocation);
      setForecast(forecastData);
      setSelectedForecast(null);
    } catch (err) {
      console.error('Error fetching weather data:', err);
      setError('Failed to fetch weather data. Please try again.');
      setCurrentWeather(null);
      setForecast(null);
    } finally {
      setLoading(false);
    }
  }, [location]);

  const handleGetCurrentLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0
        });
      });
      
      const { latitude, longitude } = position.coords;
      
      // Get weather by coordinates
      const weatherData = await weatherService.getWeatherByCoords(latitude, longitude);
      setCurrentWeather(weatherData);
      setLocation(weatherData.location); // Update location field
      
      // Get forecast data
      const forecastData = await weatherService.getForecast(weatherData.location);
      setForecast(forecastData);
      setSelectedForecast(null);
    } catch (err) {
      console.error('Error getting location or weather:', err);
      if (err.code === 1) { // Permission denied
        setError('Location permission denied. Please enter a location manually.');
      } else {
        setError('Failed to get your location. Please try again or enter a location manually.');
      }
    } finally {
      setLoading(false);
    }
  }, []);
  
  const handleCopyWeatherInfo = useCallback(() => {
    if (!currentWeather) return;
    
    const formattedWeather = weatherService.formatWeatherNotes(currentWeather);
    navigator.clipboard.writeText(formattedWeather);
    setCopySuccess(true);
  }, [currentWeather]);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getWeatherIcon = (condition) => {
    if (!condition) return <WbSunnyIcon />;
    
    condition = condition.toLowerCase();
    
    if (condition.includes('cloud')) return <WbCloudyIcon />;
    if (condition.includes('rain') || condition.includes('shower')) return <UmbrellaIcon />;
    if (condition.includes('snow') || condition.includes('ice')) return <AcUnitIcon />;
    if (condition.includes('thunder') || condition.includes('storm')) return <ThunderstormIcon />;
    
    return <WbSunnyIcon />;
  };

  const handleSelect = () => {
    if (selectedForecast && forecast) {
      const formatted = weatherService.formatForecastNotes(
        selectedForecast.slot,
        forecast.location,
        selectedForecast.dateKey
      );
      onSelectWeather(formatted);
      onClose();
      return;
    }
    if (currentWeather) {
      const formattedWeather = weatherService.formatWeatherNotes(currentWeather);
      // Append Sunrise/Sunset to ensure downstream parsing/persistence
      const sunriseLine = currentWeather.sunrise ? ` Sunrise: ${currentWeather.sunrise}.` : '';
      const sunsetLine  = currentWeather.sunset  ? ` Sunset: ${currentWeather.sunset}.`   : '';
      const withSun = `${formattedWeather}${sunriseLine}${sunsetLine}`.trim();
      onSelectWeather(withSun);
      onClose();
    }
  };

  // Render forecast items for a specific day
  const renderForecastDay = (dayData, date) => {
    if (!dayData || dayData.length === 0) return null;
    
    return (
      <Box key={date} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {new Date(date).toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
        </Typography>
        <Grid container spacing={1}>
          {dayData.map((timeSlot, index) => {
            const isSelected = selectedForecast && selectedForecast.dateKey === date && selectedForecast.slot?.time === timeSlot.time;
            return (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Paper 
                elevation={isSelected ? 4 : 1} 
                sx={{ 
                  p: 1, 
                  textAlign: 'center',
                  bgcolor: isSelected ? (theme.palette.mode === 'dark' ? 'rgba(60, 60, 80, 0.8)' : '#e8f0ff') : (theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.6)' : 'rgba(255, 255, 255, 0.9)'),
                  borderRadius: 1,
                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '1px solid transparent',
                  cursor: 'pointer'
                }}
                onClick={() => setSelectedForecast({ dateKey: date, slot: timeSlot })}
              >
                <Typography variant="caption" display="block">
                  {timeSlot.time.split(' ')[0]}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <img 
                    src={timeSlot.iconUrl} 
                    alt={timeSlot.description} 
                    style={{ width: 40, height: 40 }} 
                  />
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {timeSlot.temperature}°F
                </Typography>
                <Typography variant="caption" display="block">
                  {timeSlot.condition}
                </Typography>
              </Paper>
            </Grid>
          );})}
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      PaperProps={{
        sx: { 
          maxHeight: '90vh',
          height: 'auto'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Weather Forecast</Typography>
          {copySuccess && (
            <Chip 
              label="Copied to clipboard" 
              color="success" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            label="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Enter city, state, or zip code"
            variant="outlined"
            sx={{ mr: 1 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
          />
          <Tooltip title="Use my current location">
            <IconButton 
              onClick={handleGetCurrentLocation}
              disabled={loading}
              sx={{ ml: 1 }}
              aria-label="Use my current location"
            >
              <MyLocationIcon />
            </IconButton>
          </Tooltip>
          <Button 
            variant="contained" 
            onClick={() => handleSearch()}
            disabled={loading || !location.trim()}
            sx={{ ml: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Search'}
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {currentWeather && (
          <>
            <Paper 
              elevation={2} 
              sx={{ 
                p: 2, 
                mb: 3,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 35, 0.8)' : '#f5f5f5',
                position: 'relative'
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Tooltip title="Copy weather information">
                  <IconButton 
                    size="small" 
                    onClick={handleCopyWeatherInfo}
                    aria-label="Copy weather information"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="h6">
                    Current Weather in {currentWeather.location}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Last updated: {new Date(currentWeather.timestamp).toLocaleString()}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', textAlign: 'center' }}>
                    <img 
                      src={currentWeather.iconUrl} 
                      alt={currentWeather.description} 
                      style={{ width: 64, height: 64 }} 
                    />
                    <Typography sx={{ mt: 1 }}>
                      {currentWeather.condition}
                    </Typography>
                    <Typography variant="caption">
                      {currentWeather.description}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {currentWeather.temperature}°F
                  </Typography>
                  <Typography variant="body2">
                    Feels like: {currentWeather.feelsLike}°F
                  </Typography>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Typography variant="body2">
                    <strong>Humidity:</strong> {currentWeather.humidity}%
                  </Typography>
                  <Typography variant="body2">
                    <strong>Wind:</strong> {currentWeather.windSpeed} mph
                  </Typography>
                </Grid>
                
                <Grid item xs={6} md={3}>
                  <Typography variant="body2">
                    <strong>Sunrise:</strong> {currentWeather.sunrise}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Sunset:</strong> {currentWeather.sunset}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
            
            {forecast && (
              <>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="scrollable"
                    scrollButtons="auto"
                    aria-label="Weather forecast tabs"
                  >
                    <Tab 
                      icon={<TodayIcon />} 
                      label="Today" 
                      id="weather-tab-0" 
                      aria-controls="weather-tabpanel-0" 
                    />
                    <Tab 
                      icon={<CalendarMonthIcon />} 
                      label="5-Day Forecast" 
                      id="weather-tab-1" 
                      aria-controls="weather-tabpanel-1" 
                    />
                  </Tabs>
                </Box>
                
                <Box 
                  role="tabpanel"
                  hidden={activeTab !== 0}
                  id="weather-tabpanel-0"
                  aria-labelledby="weather-tab-0"
                >
                  {activeTab === 0 && forecast.forecast && Object.keys(forecast.forecast).length > 0 && (
                    renderForecastDay(forecast.forecast[Object.keys(forecast.forecast)[0]], Object.keys(forecast.forecast)[0])
                  )}
                </Box>
                
                <Box 
                  role="tabpanel"
                  hidden={activeTab !== 1}
                  id="weather-tabpanel-1"
                  aria-labelledby="weather-tab-1"
                  sx={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  {activeTab === 1 && forecast.forecast && (
                    Object.keys(forecast.forecast).map(date => (
                      renderForecastDay(forecast.forecast[date], date)
                    ))
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSelect}
          disabled={!currentWeather}
          variant="contained"
          color="primary"
        >
          Use This Weather
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeatherSearch;
